import { plural, t } from '@lingui/macro';

export interface ImportOutcome {
  added: number;
  failed: number;
  /** Depot resets only; the other entities derive their outcome from counts. */
  outcome?: DepotResetOutcomeEnum;
}

export interface ToastCopy {
  intent: 'success' | 'warning' | 'danger';
  title: string;
  message: string;
}

/**
 * Copy for the three outcomes, per entity.
 *
 * Written out per entity rather than interpolating "Depots"/"Contacts"/
 * "Assets" into one template so each reads naturally and translators get nine
 * real sentences instead of one with a placeholder noun.
 *
 * "Nothing added" covers both an import where every row failed and one that
 * fell over before processing anything; the ticket asks for a single message
 * for both.
 */

const retryMessage = () =>
  t`Try again, or contact support if the issue persists.`;

export function depotToastCopy({ added, failed }: ImportOutcome): ToastCopy {
  if (added === 0) {
    return {
      intent: 'danger',
      title: t`Depots failed to add due to unexpected error.`,
      message: retryMessage(),
    };
  }

  if (failed > 0) {
    return {
      intent: 'warning',
      title: t`Depots partially added.`,
      message: t`${added} added, ${failed} failed. Download the report for details.`,
    };
  }

  return {
    intent: 'success',
    title: t`Depots added successfully.`,
    message: plural(added, {
      one: '# depot was added successfully.',
      other: '# depots were added successfully.',
    }),
  };
}

export function contactToastCopy({ added, failed }: ImportOutcome): ToastCopy {
  if (added === 0) {
    return {
      intent: 'danger',
      title: t`Contacts failed to add due to unexpected error.`,
      message: retryMessage(),
    };
  }

  if (failed > 0) {
    return {
      intent: 'warning',
      title: t`Contacts partially added.`,
      message: t`${added} added, ${failed} failed. Download the report for details.`,
    };
  }

  return {
    intent: 'success',
    title: t`Contacts added successfully.`,
    message: plural(added, {
      one: '# contact was added successfully.',
      other: '# contacts were added successfully.',
    }),
  };
}

export function assetToastCopy({ added, failed }: ImportOutcome): ToastCopy {
  if (added === 0) {
    return {
      intent: 'danger',
      title: t`Assets failed to add due to unexpected error.`,
      message: retryMessage(),
    };
  }

  if (failed > 0) {
    return {
      intent: 'warning',
      title: t`Assets partially added.`,
      message: t`${added} added, ${failed} failed. Download the report for details.`,
    };
  }

  return {
    intent: 'success',
    title: t`Assets added successfully.`,
    message: plural(added, {
      one: '# asset was added successfully.',
      other: '# assets were added successfully.',
    }),
  };
}

export function depotResetToastCopy({
  added,
  failed,
  outcome,
}: ImportOutcome): ToastCopy {
  if (outcome === 'SUCCESS') {
    return {
      intent: 'success',
      title: t`Depots successfully reset.`,
      // No depot summaries means no count worth showing.
      message:
        added === 0
          ? t`The depot reset completed successfully.`
          : plural(added, {
              one: '# depot was reset successfully.',
              other: '# depots were reset successfully.',
            }),
    };
  }

  if (outcome === 'PARTIAL_SUCCESS') {
    return {
      intent: 'warning',
      title: t`Depots partially reset.`,
      message: t`${added} reset, ${failed} with errors. Download the report for details.`,
    };
  }

  return {
    intent: 'danger',
    title: t`The depot reset could not be completed.`,
    message: retryMessage(),
  };
}

/** Copy for an upload the API stopped after it ran too long. */
export function stoppedToastCopy(importType?: string): ToastCopy {
  const message = t`It ran too long and the rows it had not finished need manual intervention. Contact support for help.`;

  switch (importType) {
    case 'depot_bulk_import':
      return { intent: 'danger', title: t`Depot upload stopped.`, message };
    case 'contact_bulk_import':
      return { intent: 'danger', title: t`Contact upload stopped.`, message };
    case 'asset_bulk_upload':
      return { intent: 'danger', title: t`Asset upload stopped.`, message };
    case 'depot_reset':
      return { intent: 'danger', title: t`Depot reset stopped.`, message };
    default:
      return { intent: 'danger', title: t`Upload stopped.`, message };
  }
}
